import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { FaDownload, FaLock, FaExclamationTriangle, FaGithub, FaFileWord, FaFilePdf } from 'react-icons/fa';
import Navbar from '../../components/public/Navbar';
import api from '../../services/api';
import { toast } from 'react-toastify';

const DownloadProject = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError("No download token provided.");
      setLoading(false);
      return;
    }

    const checkToken = async () => {
      try {
        const res = await api.get(`/project-orders/token-info?token=${token}`);
        if (res.data.status && res.data.data) {
          setInfo(res.data.data);
        } else {
          setError("Invalid token response.");
        }
      } catch (err) {
        setError(err.response?.data?.message || "This secure link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    };

    checkToken();
  }, [token]);

  const downloadFile = async (endpoint, filenamePrefix) => {
    try {
      const response = await api.get(`/project-orders/${endpoint}?token=${token}`, {
        responseType: 'blob' // Important for downloading files
      });
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Extract filename from headers if possible
      let filename = `${filenamePrefix}_${info?.title?.replace(/\s+/g, '_') || 'file'}`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) { 
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      decrementCount();
    } catch (err) {
      if (err.response && err.response.data && err.response.data instanceof Blob) {
        // Parse the blob error response to JSON
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          toast.error(json.message || "Failed to download file");
        } catch (e) {
          toast.error("Failed to download file");
        }
      } else {
        toast.error("Failed to download file. Please try again.");
      }
    }
  };

  const handleDownload = () => {
    downloadFile('download', 'Project');
  };

  const handleDownloadDocx = () => {
    downloadFile('download-docx', 'Report');
  };

  const handleDownloadPdf = () => {
    downloadFile('download-pdf', 'Report');
  };

  const decrementCount = () => {
    // Decrement local remaining count after a short delay to reflect the download
    setTimeout(() => {
      setInfo(prev => prev ? { ...prev, downloads_remaining: prev.downloads_remaining - 1 } : prev);
    }, 2000);
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100">
        <Navbar />
        <Container className="text-center" style={{ paddingTop: '100px' }}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Verifying secure link...</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <Navbar />
      <Container style={{ paddingTop: '100px', maxWidth: '600px' }}>
        <Card className="shadow-lg border-0 rounded-4 overflow-hidden mb-5">
          <div className="bg-primary text-white text-center py-4">
            <FaLock size={40} className="mb-2" />
            <h4 className="mb-0 fw-bold">Secure Project Delivery</h4>
          </div>
          
          <Card.Body className="p-4 p-md-5">
            {error ? (
              <Alert variant="danger" className="text-center py-4">
                <FaExclamationTriangle size={30} className="mb-3" />
                <h5>Access Denied</h5>
                <p className="mb-0">{error}</p>
                <hr />
                <small>Links expire for security purposes. If you believe this is an error, please contact support.</small>
              </Alert>
            ) : info && info.valid ? (
              <div className="text-center">
                <h5 className="mb-4">Your file is ready for download.</h5>
                
                <Alert variant="warning" className="text-start mb-4 shadow-sm border-warning">
                  <strong><FaExclamationTriangle className="me-2" /> Security Notice</strong>
                  <ul className="mb-0 mt-2 text-muted small">
                    <li>This link is tied to your purchase and will expire on <strong>{new Date(info.expires_at).toLocaleString()}</strong>.</li>
                    <li>You have <strong>{info.downloads_remaining} downloads remaining</strong>.</li>
                    <li>Do not share this link. Once the limit is reached, this page will be locked.</li>
                  </ul>
                </Alert>

                <div className="bg-light border rounded p-4 mb-4">
                  <h6 className="text-muted text-uppercase mb-2 small">Project Title</h6>
                  <h4 className="fw-bold mb-0 text-primary">{info.title}</h4>
                </div>
                
                {info.github_url && (
                  <Button 
                    variant="dark" 
                    size="lg" 
                    className="w-100 fw-bold shadow py-3 mb-3"
                    href={info.github_url}
                    target="_blank"
                  >
                    <FaGithub className="me-2" /> View GitHub Repository
                  </Button>
                )}
                
                <Button 
                  variant="success" 
                  size="lg" 
                  className="w-100 fw-bold shadow py-3 mb-3"
                  onClick={handleDownload}
                  disabled={info.downloads_remaining <= 0}
                >
                  <FaDownload className="me-2" /> Download ZIP File
                </Button>

                {info.has_docx && (
                  <Button 
                    variant="outline-primary" 
                    size="lg" 
                    className="w-100 fw-bold shadow-sm py-3 mb-3 d-flex justify-content-center align-items-center"
                    onClick={handleDownloadDocx}
                    disabled={info.downloads_remaining <= 0}
                  >
                    <FaFileWord className="me-2" /> Download Project Report (DOCX)
                  </Button>
                )}

                {info.has_pdf && (
                  <Button 
                    variant="outline-danger" 
                    size="lg" 
                    className="w-100 fw-bold shadow-sm py-3 d-flex justify-content-center align-items-center"
                    onClick={handleDownloadPdf}
                    disabled={info.downloads_remaining <= 0}
                  >
                    <FaFilePdf className="me-2" /> Download Project Report (PDF)
                  </Button>
                )}
              </div>
            ) : (
              <Alert variant="danger" className="text-center">
                This secure link has expired or reached its maximum download limit.
              </Alert>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default DownloadProject;
